/**
 * `useRowIndexMap` — the one reconciliation between the three index spaces a
 * windowed, deduplicated, paged table juggles. The preset routes its windowed
 * render (`virtualToLogical`), `aria-rowindex`/`aria-rowcount`, keyboard
 * scroll-into-view (`logicalToVirtual`), and the infinite-fetch frontier
 * (`serverOffsetCount`) through it, so no sibling table re-derives the offset math.
 *
 * The three spaces:
 *   - **server offset** — raw, pre-dedup position in the flat `pages[].items`
 *     concatenation; what a deep-link speaks and the server cursor counts.
 *   - **logical index** — position AFTER first-occurrence dedup; what the TanStack
 *     row model and virtualizer see. Dedup makes it diverge from server offset
 *     (drop a duplicate at offset 3 and offset 4 becomes logical 3) — the entire
 *     reason this map exists.
 *   - **virtual index** — `virtualItem.index`. Identity with logical under the
 *     dense count model; kept a separate step so the sparse model (virtualizer
 *     counts `serverTotal` placeholder slots, not loaded rows) can reinterpret it
 *     without touching callers.
 *
 * `deriveAriaRowCount` is the isolated count-model seam: flipping sparse→dense
 * reworks only it, leaving every translation put.
 */
import { useMemo } from 'react'

/** ARIA rowcount sentinel for "total not yet known" (WAI-ARIA `aria-rowcount`). */
export const ARIA_ROWCOUNT_UNKNOWN = -1

/** Count model for `aria-rowcount`: `sparse` (server total, `-1` when unknown) or `dense` (loaded count). */
export type RowCountModel = 'sparse' | 'dense'

export type UseRowIndexMapOptions<TData> = {
  /**
   * RAW, pre-dedup row stream (flat `pages[].items`). The map owns the dedup —
   * feeding an already-deduped array throws away the duplicates' raw offsets and
   * collapses every translation to identity. Collapse matches the engine's
   * `dedupeRows` (first occurrence wins) so logical indices agree without the two
   * sharing an array. MUST be reference-stable per render or the memo re-runs.
   */
  rows: TData[]
  /** Stable id extractor — the SAME key the engine dedups on, so both agree on identity. */
  getRowId: (row: TData) => string
  /** Server total across all pages; `undefined` while unknown. Drives the sparse `aria-rowcount`. */
  serverTotal?: number
  /** Whether more pages remain to load. */
  hasNextPage?: boolean
  /**
   * 1-based rows the header occupies, added to the logical index for `aria-rowindex`
   * (WAI-ARIA is 1-based: header is index 1, first body row 2). Defaults to 1.
   */
  headerRowCount?: number
  /** Count model for `aria-rowcount`. Defaults to sparse. */
  countModel?: RowCountModel
}

export type RowIndexMap = {
  /** Loaded, deduped (logical) row count. */
  readonly logicalCount: number
  /** Raw server rows seen before dedup — diverges from `logicalCount` by the dropped-duplicate count. */
  readonly serverOffsetCount: number

  // virtual ⇄ logical — model-aware: identity in `dense`; the server-offset
  // reconciliation in `sparse`, where the virtualizer lays out `serverTotal` slots.
  /** Virtualizer slot index → logical row index (`-1` = an unloaded sparse placeholder slot). */
  virtualToLogical: (virtualIndex: number) => number
  /** Logical row index → virtualizer slot index (its server offset in `sparse`). */
  logicalToVirtual: (logicalIndex: number) => number

  // logical ⇄ server offset — where dedup makes them diverge.
  /** Logical index → first server offset that produced it (kept occurrence); `-1` if out of range. */
  logicalToServerOffset: (logicalIndex: number) => number
  /** Server offset → logical index it collapses into (a duplicate resolves to its first occurrence); `-1` if unloaded. */
  serverOffsetToLogical: (serverOffset: number) => number

  /** `aria-rowindex` for a logical row (1-based logical + header offset); count-model-agnostic. */
  getAriaRowIndex: (logicalIndex: number) => number
  /** `aria-rowcount` — the isolated count-model seam; the only fn that reworks on a sparse→dense flip. */
  getAriaRowCount: () => number

  /**
   * Server offset → deduped logical index, for a consumer deep-linking by server
   * offset. `-1` when the offset is in an unloaded page (caller decides whether to
   * fetch-then-scroll).
   */
  resolveScrollIndex: (serverOffset: number) => number
  /**
   * Whether an id is in the loaded (logical) window. The cross-page-selection seam:
   * selection is keyed by id, and this distinguishes a loaded row from one selected
   * via a prior "select all N" but not in the DOM, so the header count stays consistent.
   */
  isLoaded: (id: string) => boolean
}

/**
 * The isolated count-model seam — the only function a sparse→dense flip reworks.
 *
 * `aria-rowcount` MUST include the header rows it numbers (WAI-ARIA): the header is
 * `aria-rowindex` 1 and the last body row `logicalCount + headerRowCount`, so a count
 * omitting the header reports a total shorter than its largest index ("row 6 of 5").
 * Every non-sentinel branch adds `headerRowCount`; the empty state still returns
 * `headerRowCount` (not 0) because the header row renders and announces index 1.
 */
export const deriveAriaRowCount = (params: {
  countModel: RowCountModel
  logicalCount: number
  serverTotal?: number
  hasNextPage?: boolean
  headerRowCount?: number
}): number => {
  const { countModel, logicalCount, serverTotal, hasNextPage, headerRowCount = 1 } = params

  if (countModel === 'dense') {
    return logicalCount + headerRowCount
  }

  // Sparse: server total is the body-row total; header rows counted on top. Floored at
  // `logicalCount` (mirroring `resolveVirtualCount`) so an UNDER-reporting `serverTotal`
  // never announces a count below the last loaded row's `aria-rowindex` — that would be
  // "row N of <N", an invalid WAI-ARIA graph. A no-op for an accurate total.
  if (serverTotal !== undefined) {
    return Math.max(serverTotal, logicalCount) + headerRowCount
  }

  // Total unknown: `-1` while more pages remain; else degrade to the loaded count +
  // header (last page reached without a server total — don't announce a false `-1`).
  return hasNextPage === true ? ARIA_ROWCOUNT_UNKNOWN : logicalCount + headerRowCount
}

/**
 * Build the first-occurrence offset tables in one pass, mirroring the engine's
 * `dedupeRows` collapse (first occurrence wins). Every translation reads from these
 * three tables; none recompute.
 */
const buildOffsetTables = <TData>(
  rows: TData[],
  getRowId: (row: TData) => string,
): {
  logicalToOffset: number[]
  offsetToLogical: Map<number, number>
  idToLogical: Map<string, number>
  serverOffsetCount: number
} => {
  const logicalToOffset: number[] = []
  const offsetToLogical = new Map<number, number>()
  const idToLogical = new Map<string, number>()

  let serverOffset = 0
  for (const row of rows) {
    const id = getRowId(row)
    const firstLogical = idToLogical.get(id)
    if (firstLogical === undefined) {
      // First occurrence → becomes a logical row. `idToLogical` is the sole
      // "already seen" record, so a duplicate is exactly an id already in it.
      const logicalIndex = logicalToOffset.length
      idToLogical.set(id, logicalIndex)
      logicalToOffset.push(serverOffset)
      offsetToLogical.set(serverOffset, logicalIndex)
    } else {
      // Dropped duplicate → collapses into its first occurrence's logical index, so
      // `scrollToIndex(thisRawOffset)` still lands on the kept row.
      offsetToLogical.set(serverOffset, firstLogical)
    }
    serverOffset += 1
  }

  return { logicalToOffset, offsetToLogical, idToLogical, serverOffsetCount: serverOffset }
}

/**
 * The authoritative index map, memoized on reference-stable inputs (`rows` must be
 * reference-stable per render). Returns a single memoized object so consumers can
 * pass it down without destabilizing children under React Compiler.
 */
export const useRowIndexMap = <TData>(options: UseRowIndexMapOptions<TData>): RowIndexMap => {
  const {
    rows,
    getRowId,
    serverTotal,
    hasNextPage,
    headerRowCount = 1,
    countModel = 'sparse',
  } = options

  return useMemo<RowIndexMap>(() => {
    const { logicalToOffset, offsetToLogical, idToLogical, serverOffsetCount } = buildOffsetTables(
      rows,
      getRowId,
    )
    const logicalCount = logicalToOffset.length

    const logicalToServerOffset = (logicalIndex: number): number =>
      logicalToOffset[logicalIndex] ?? -1

    const serverOffsetToLogical = (serverOffset: number): number =>
      offsetToLogical.get(serverOffset) ?? -1

    // Slot⇄logical is model-aware (the module's reserved seam). `dense` — the
    // virtualizer lays out loaded rows, so a slot IS its logical index (identity).
    // `sparse` — it lays out `serverTotal` slots, so a slot is a SERVER OFFSET: loaded
    // slots resolve to their logical row, unloaded ones to -1 (the placeholder signal).
    const virtualToLogical =
      countModel === 'sparse'
        ? serverOffsetToLogical
        : (virtualIndex: number): number => virtualIndex
    const logicalToVirtual =
      countModel === 'sparse'
        ? logicalToServerOffset
        : (logicalIndex: number): number => logicalIndex

    const getAriaRowIndex = (logicalIndex: number): number => logicalIndex + 1 + headerRowCount

    const getAriaRowCount = (): number =>
      deriveAriaRowCount({ countModel, logicalCount, serverTotal, hasNextPage, headerRowCount })

    const resolveScrollIndex = (serverOffset: number): number => serverOffsetToLogical(serverOffset)

    const isLoaded = (id: string): boolean => idToLogical.has(id)

    return {
      logicalCount,
      serverOffsetCount,
      virtualToLogical,
      logicalToVirtual,
      logicalToServerOffset,
      serverOffsetToLogical,
      getAriaRowIndex,
      getAriaRowCount,
      resolveScrollIndex,
      isLoaded,
    }
  }, [rows, getRowId, serverTotal, hasNextPage, headerRowCount, countModel])
}
